import { call, put, race, takeEvery } from 'redux-saga/effects';

import { Record } from 'immutable';
import { resourceSagaUtils, resourceStoreTypes } from '../core';
import * as actions from './preferencesV2Actions';
import { getLastSyncDate } from './preferencesV2Selectors';
import { isValidJSON, saveRequestValue, saveValueIntoRedux, typeCheck } from './preferencesV2Utils';

const mkResourceConfig = (props) =>
  resourceStoreTypes.mkQcsSyncResourceConfig({
    resourceName: 'preferences-v2',
    resourceBaseUrl: '/v2/preferences',
    getLastSyncDate,
    ...props,
  });

export function* getPreferencesV2ActionWatcher() {
  const config = mkResourceConfig({
    resourceBaseUrl: '/v2/preferences',
    successAction: actions.getPreferencesV2Success,
    failureAction: actions.getPreferencesV2Failure,
    // axiosConfig: { headers: { 'qcs-dataset-id': null } },
    // bc pref-v2 names are unique, we want to key off names for fast lookups
    transformResponseToResources: (response) =>
      response?.data?.resources?.map((resource) => {
        const respObj = {
          ...resource,
          id: resource?.name,
          preferenceId: resource?.id,
          value: saveValueIntoRedux(resource?.dataType, resource?.value),
        };
        return Record(respObj)(respObj);
      }),
  });
  yield takeEvery(actions.getPreferencesV2, resourceSagaUtils.qcsSyncGetResources, config);
}

export function* getSpecificPreferencesV2ActionWatcher() {
  const config = mkResourceConfig({
    // note - this id in the payload is the preference name, not the qcs pref id
    resourceBaseUrl: (action) => (action.payload?.id ? `/v2/preferences/${action.payload.id}` : '/v2/preferences'),
    successAction: actions.getSpecificPreferenceV2Success,
    failureAction: actions.getSpecificPreferenceV2Failure,
    // we want to store prefs v2 as name: value pairs within redux for quick lookup
    transformResponseToResource: (response) => {
      const responseObj = {
        ...response?.data,
        id: response?.data?.name,
        preferenceId: response?.data?.id,
        value: saveValueIntoRedux(response?.data?.dataType, response?.data?.value),
      };
      return responseObj && Record(responseObj)(responseObj);
    },
  });
  yield takeEvery(actions.getSpecificPreferenceV2, resourceSagaUtils.qcsSyncGetResource, config);
}

export function* updatePreferenceCriteriaActionWatcher() {
  const config = mkResourceConfig({
    resourceBaseUrl: '/v2/preferences/criteria',
    successAction: actions.updatePreferenceCriteriaSuccess,
    failureAction: actions.updatePreferenceCriteriaFailure,
  });
  yield takeEvery(actions.updatePreferenceCriteria, resourceSagaUtils.qcsSyncCreateResource, config);
}

function* updatePreferenceV2Saga(configuration, action) {
  const dataType = action?.payload?.dataType;
  let isValid = true;
  if (dataType && (dataType === 'JSON' || dataType === 'ARRAY')) {
    isValid = !!isValidJSON(action?.payload?.value);
  }
  if (isValid) {
    yield call(resourceSagaUtils.qcsSyncCreateResource, configuration, action);
  }
}

export function* updatePreferenceOverrideActionWatcher() {
  const config = mkResourceConfig({
    resourceBaseUrl: () => '/v2/preferences/override',
    successAction: actions.updatePreferenceOverrideSuccess,
    failureAction: actions.updatePreferenceOverrideFailure,
    transformResourceWithResponse: null,
    // adding id here results in a 400 error due to override having a separate id not accessible to the client
    transformResourceToRequestData: (request) => ({
      ...(request?.toJS?.() || request),
      value: saveRequestValue(request),
      preferenceName: request.preferenceName || request.id,
      id: undefined,
    }),
  });
  yield resourceSagaUtils.takeAndDebounce(actions.updatePreferenceOverride, updatePreferenceV2Saga, config);
}

function* resetPreferenceSaga(configuration, action) {
  yield call(resourceSagaUtils.qcsSyncDeleteResource, configuration, action);
  // note - this id is the preference name
  const { id } = action.payload;
  const { resetPreferenceOverrideSuccess } = yield race({
    resetPreferenceOverrideSuccess: actions.resetPreferenceOverrideSuccess,
    resetPreferenceOverrideFailure: actions.resetPreferenceOverrideFailure,
  });
  if (resetPreferenceOverrideSuccess) {
    yield put(actions.getSpecificPreferenceV2({ id }));
  }
}

export function* resetPreferenceOverrideActionWatcher() {
  const config = mkResourceConfig({
    resourceBaseUrl: (action) =>
      `/v2/preferences/override/${action.payload.preferenceId}/${
        typeCheck(action.payload.value) || action.payload.value
      }`,
    successAction: actions.resetPreferenceOverrideSuccess,
    failureAction: actions.resetPreferenceOverrideFailure,
    transformResourceWithResponse: null,
    // adding id results in a 400 error bc this endpoint expects override id which we do not have access to
    transformResourceToRequestData: (request) => ({
      ...(request.toJS ? request.toJS() : request),
      value: saveRequestValue(request),
      id: null,
    }),
  });
  yield takeEvery(actions.resetPreferenceOverride, resetPreferenceSaga, config);
}

export default [
  getPreferencesV2ActionWatcher,
  resetPreferenceOverrideActionWatcher,
  updatePreferenceCriteriaActionWatcher,
  updatePreferenceOverrideActionWatcher,
  getSpecificPreferencesV2ActionWatcher,
];
